<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <Multiselect
              v-model="rawFocusPoint.campaign"
              :error="errors?.campaign"
              class="col-start-1 col-end-2"
              :options="campaigns"
              :label="$t('focusPoint.campaign')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              @select="selectCampaign()"
            />
            <Multiselect
              v-if="isNew"
              v-model="rawFocusPoint.template"
              :error="errors?.template"
              class="col-start-1 col-end-2"
              :options="focusPointTemplates"
              :label="$t('focusPoint.template')"
              :can-deselect="true"
              :can-clear="true"
              :searchable="true"
              :object="true"
              :loading="loadingFocusPointTemplates"
              :disabled="!rawFocusPoint.campaign"
              @select="selectTemplate"
            />
            <FormInput
              v-model="rawFocusPoint.name"
              :error="errors?.name"
              class="col-start-1 col-end-2"
              type="text"
              :label="$t('focusPoint.name')"
              :placeholder="$t('focusPoint.name')"
            />
            <LocationMap ref="map" v-model="rawFocusPoint.gisLocation" v-model:radius="rawFocusPoint.radius" />
            <div class="flex gap-x-5">
              <FormInput
                :model-value="rawFocusPoint.gisLocation?.lat"
                :error="errors?.gisLocation?.lat"
                class="w-1/2"
                type="text"
                :label="$t('lat')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(parseFloat($event), rawFocusPoint.gisLocation?.long)"
              />
              <FormInput
                :model-value="rawFocusPoint.gisLocation?.long"
                :error="errors?.gisLocation?.long"
                class="w-1/2"
                type="text"
                :label="$t('long')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(rawFocusPoint.gisLocation?.lat, parseFloat($event))"
              />
            </div>
            <FormInput
              v-model="rawFocusPoint.radius"
              :error="errors?.radius"
              class="col-start-1 col-end-2"
              type="number"
              :label="$t('focusPoint.radius')"
              :placeholder="$t('focusPoint.radius')"
            />
            <div class="grid grid-cols-2 gap-x-5">
              <FormInput
                v-model="rawFocusPoint.cardinality"
                :error="errors?.cardinality"
                type="number"
                :label="$t('focusPoint.cardinality')"
                :placeholder="$t('focusPoint.cardinality')"
              />
              <FormInput
                v-model="rawFocusPoint.paymentAmount"
                :error="errors?.paymentAmount"
                type="number"
                :label="$t('focusPoint.paymentAmount')"
                :placeholder="$t('focusPoint.paymentAmount')"
              />
            </div>
            <div class="grid grid-cols-2 gap-x-5">
              <InputDuration
                v-model="rawFocusPoint.threshold"
                :error="errors?.threshold"
                type="text"
                class="w-full"
                :label="$t('focusPoint.threshold')"
                placeholder="hh:mm:ss"
              />
              <FormInput
                v-model="rawFocusPoint.numOfPhotos"
                :error="errors?.numOfPhotos"
                type="number"
                :label="$t('focusPoint.numOfPhotos')"
                :placeholder="$t('focusPoint.numOfPhotos')"
              />
            </div>
            <div class="grid grid-cols-2 gap-x-5">
              <DatePicker
                v-model="rawFocusPoint.validityStart"
                :error="errors?.validityStart"
                mode="datetime"
                :label="$t('focusPoint.validityStart')"
              />
              <DatePicker
                v-model="rawFocusPoint.validityEnd"
                :error="errors?.validityEnd"
                mode="datetime"
                :label="$t('focusPoint.validityEnd')"
              />
            </div>
          </div>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  date, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import InputDuration from '@utils/inputs/InputDuration.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import type {
  Campaign, CampaignGroup, FocusPoint, FocusPointTemplate,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
  focusPoint?: FocusPoint;
}

const emit = defineEmits(['update:open', 'update:focusPoint', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalFocusPoint = useVModel(props, 'focusPoint', emit);

const isNew = computed(() => !props.focusPoint);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.campaigns.focusPointList.modal.new'
    : 'pages.campaigns.focusPointList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const groupId = computed<{ group?: string }>(() =>
  (props.campaignGroup?.id ? { group: props.campaignGroup.id } : {}));

const params = reactive({
  filter: groupId,
});

const fetchCampaigns = collOptionsProvider('/campaigns', params, (value: Campaign) => ({
  label: value.name,
  value: value['@id'],
  gisLocation: value.region?.gisLocation,
  region: value.region,
}));
const campaigns = ref<{ label: string, value: string|null, gisLocation?: { lat: number, long: number } }[]>([]);
const loadCampaigns = async () => {
  campaigns.value = await fetchCampaigns();
};
if (props.campaignGroup?.id) {
  loadCampaigns();
}
watch(groupId, loadCampaigns, { immediate: false });

const initialData = (focusPoint?: FocusPoint) =>
  (focusPoint
    ? {
      ...focusPoint,
      template: {
        label: focusPoint.template?.name,
        value: focusPoint.template?.['@id'],
        item: focusPoint.template,
      },
    }
    : {
      campaign: undefined,
      name: undefined,
      radius: undefined,
      threshold: undefined,
      cardinality: undefined,
      paymentAmount: undefined,
      numOfPhotos: undefined,
      gisLocation: undefined,
      validityStart: undefined,
      validityEnd: undefined,
      template: undefined,
    });

const rawFocusPoint = syncRef(internalFocusPoint, (focusPoint) => ({
  ...initialData(focusPoint),
}));

const isPrice = (value: undefined|number) => {
  if (value !== undefined) {
    return /^\d+(\.\d{0,2})?$/.test(value.toString());
  }
  return true;
};

const {
  data: focusPoint,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    campaign: string().nullable().required(t('validation.notEmpty')),
    name: string().nullable().required(t('validation.notEmpty')),
    radius: number()
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    threshold: number()
      .integer(t('validation.integer'))
      .positive(t('validation.positive'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    cardinality: number()
      .integer(t('validation.integer'))
      .positive(t('validation.positive'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    paymentAmount: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .test('is-price', t('validation.price'), isPrice)
      .nullable()
      .required(t('validation.notEmpty')),
    numOfPhotos: number()
      .integer(t('validation.integer'))
      .positive(t('validation.positive'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    gisLocation: object({
      lat: number().nullable().required(t('validation.notEmpty')),
      long: number().nullable().required(t('validation.notEmpty')),
    }),
    validityStart: date().nullable(),
    validityEnd: date().nullable(),
  }),
  rawFocusPoint,
);

function resetData() {
  rawFocusPoint.value = { ...initialData() } as unknown as typeof focusPoint['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalFocusPoint.value = undefined;
}

const focusPointTemplates = ref<{ label: string, value: string, item: FocusPointTemplate}>([]);
const loadingFocusPointTemplates = ref(false);
const loadFocusPointTemplates = async (regionId: string) => {
  loadingFocusPointTemplates.value = true;
  try {
    focusPointTemplates.value = await collOptionsProvider('/focus_point_templates', { filter: { region: regionId } }, (value: FocusPointTemplate) => ({
      label: value.name,
      value: value['@id'],
      item: value,
    }))();
  } finally {
    loadingFocusPointTemplates.value = false;
  }
};

const map = ref();

const setMapCenter = () => {
  const gisLocation = campaigns.value.find((campaign) => campaign.value === rawFocusPoint.value?.campaign)?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const selectCampaign = () => {
  setMapCenter();
  loadFocusPointTemplates(campaigns.value.find((campaign) => campaign.value === rawFocusPoint.value?.campaign)?.region?.id);
};

const selectTemplate = () => {
  rawFocusPoint.value.name = rawFocusPoint.value.template?.item?.name ?? undefined;
  rawFocusPoint.value.radius = rawFocusPoint.value.template?.item?.radius ?? undefined;
  rawFocusPoint.value.threshold = rawFocusPoint.value.template?.item?.threshold ?? undefined;
  rawFocusPoint.value.cardinality = rawFocusPoint.value.template?.item?.cardinality ?? undefined;
  rawFocusPoint.value.paymentAmount = rawFocusPoint.value.template?.item?.paymentAmount ?? undefined;
  rawFocusPoint.value.numOfPhotos = rawFocusPoint.value.template?.item?.numOfPhotos ?? undefined;
  rawFocusPoint.value.validityStart = rawFocusPoint.value.template?.item?.validityStart ?? undefined;
  rawFocusPoint.value.validityEnd = rawFocusPoint.value.template?.item?.validityEnd ?? undefined;
  rawFocusPoint.value.gisLocation = rawFocusPoint.value.template?.item?.gisLocation ?? undefined;
};

const setCoords = (lat: number|undefined, long: number|undefined) => {
  rawFocusPoint.value.gisLocation = {
    lat: lat || 0,
    long: long || 0,
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.focusPoint;
    const url = edit ? props.focusPoint['@id'] : '/focus_points';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        campaign: focusPoint.value.campaign,
        name: focusPoint.value.name,
        radius: focusPoint.value.radius,
        threshold: focusPoint.value.threshold,
        cardinality: focusPoint.value.cardinality,
        paymentAmount: focusPoint.value.paymentAmount,
        numOfPhotos: focusPoint.value.numOfPhotos,
        gisLocation: focusPoint.value.gisLocation,
        validityStart: focusPoint.value.validityStart,
        validityEnd: focusPoint.value.validityEnd,
        template: focusPoint.value.template?.value,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
